import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';

const AboutCTA = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "center center"]
  });

  // Container reveal
  const containerOpacity = useTransform(scrollYProgress, [0, 0.4], [0, 1]);
  const containerY = useTransform(scrollYProgress, [0, 0.4], [100, 0]);
  const containerScale = useTransform(scrollYProgress, [0, 0.4], [0.95, 1]);

  // Staggered content reveals
  const headlineOpacity = useTransform(scrollYProgress, [0.1, 0.45], [0, 1]);
  const headlineY = useTransform(scrollYProgress, [0.1, 0.45], [40, 0]);

  const descriptionOpacity = useTransform(scrollYProgress, [0.15, 0.5], [0, 1]);
  const descriptionY = useTransform(scrollYProgress, [0.15, 0.5], [40, 0]);

  const buttonOpacity = useTransform(scrollYProgress, [0.2, 0.55], [0, 1]);
  const buttonY = useTransform(scrollYProgress, [0.2, 0.55], [40, 0]);

  const trustOpacity = useTransform(scrollYProgress, [0.25, 0.6], [0, 1]);

  // Background glow intensity
  const glowIntensity = useTransform(scrollYProgress, [0, 0.3, 0.7], [0.05, 0.2, 0.1]);

  return (
    <section ref={containerRef} className="relative bg-dark-bg py-32 md:py-48 overflow-hidden">
      {/* Continuous Background Grid */}
      <div className="absolute inset-0 pointer-events-none opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0, 102, 255, 0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0, 102, 255, 0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Dynamic Background Glow */}
      <motion.div 
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent-blue rounded-full blur-[120px] pointer-events-none"
        style={{ opacity: glowIntensity }}
      />

      <div className="relative z-10 max-w-[1000px] mx-auto px-6 md:px-8">
        <motion.div
          style={{ opacity: containerOpacity, y: containerY, scale: containerScale }}
          className="text-center"
        >
          <motion.h2
            style={{ opacity: headlineOpacity, y: headlineY }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 md:mb-8 leading-tight px-4 md:px-0"
          >
            Work with us
          </motion.h2>

          <motion.p
            style={{ opacity: descriptionOpacity, y: descriptionY }}
            className="text-base md:text-xl lg:text-2xl text-gray-400 leading-relaxed mb-10 md:mb-12 max-w-[600px] md:max-w-[800px] mx-auto px-4 md:px-0"
          >
            We work with founder-led companies generating $1M–$100M in revenue. If you are ready to identify and remove your biggest bottleneck, let's talk.
          </motion.p>

          <motion.div style={{ opacity: buttonOpacity, y: buttonY }}>
            <Link
              to="/apply"
              className="inline-block min-h-[56px] px-8 md:px-10 py-4 bg-accent-blue text-white text-base md:text-lg font-medium rounded-full hover:brightness-110 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_60px_-15px_rgba(0,102,255,0.5)] whitespace-nowrap cursor-pointer"
            >
              Apply to Work Together
            </Link>
          </motion.div>

          {/* Trust indicator */}
          <motion.p
            style={{ opacity: trustOpacity }}
            className="mt-8 md:mt-10 text-sm md:text-base text-gray-500"
          >
            Typical response time: 48 hours
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutCTA;